import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import DrowningAlertCard from '@/components/teacher/DrowningAlertCard';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  LogOut, School as SchoolIcon, Users, RefreshCw, GraduationCap, Inbox, BarChart3,
  FileText,
} from 'lucide-react';

const ACADEMIC_YEARS = ['2025-26', '2026-27', '2027-28'];
const DEFAULT_AY = '2026-27';

/**
 * School Teacher Home — Ticket 1A.6α.
 * Read-only dashboard listing the teacher's assigned classes for an AY.
 * Student List + chat with students arrives in 1A.7 / 1A.6β.
 */
export default function SchoolTeacherHome() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [school, setSchool] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [filterYear, setFilterYear] = useState(DEFAULT_AY);
  const [bootLoading, setBootLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);

  const loadAssignments = useCallback(async (ay) => {
    setListLoading(true);
    try {
      const res = await api.get('/teacher/me/assignments', {
        params: { academic_year: ay, status: 'active' },
      });
      setAssignments(res.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load classes');
      setAssignments([]);
    } finally {
      setListLoading(false);
    }
  }, []);

  // Initial mount: fetch me + school in parallel, then assignments
  useEffect(() => {
    (async () => {
      try {
        const meRes = await api.get('/auth/me');
        setMe(meRes.data);
        if (meRes.data?.school_id) {
          const schoolRes = await api.get(`/schools/${meRes.data.school_id}`);
          setSchool(schoolRes.data);
        }
        await loadAssignments(DEFAULT_AY);
      } catch (err) {
        console.warn('[SchoolTeacherHome] init failed (route guard handles auth):', err);
      } finally {
        setBootLoading(false);
      }
    })();
  }, [loadAssignments]);

  const handleYearChange = (ay) => {
    setFilterYear(ay);
    loadAssignments(ay);
  };

  const handleRefresh = () => loadAssignments(filterYear);

  const handleLogout = async () => {
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    localStorage.removeItem('yukti_session_token');
    const slug = school?.slug;
    navigate(slug ? `/school/${slug}/login` : '/', { replace: true });
  };

  const brandColor = school?.brand_color || '#4F46E5';
  const teacherName = useMemo(() => {
    if (!me?.name) return '';
    return me.name.split(' ')[0];
  }, [me]);

  if (bootLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div
          className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"
          data-testid="school-teacher-boot-spinner"
        />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50"
      data-testid="school-teacher-home"
    >
      {/* Header */}
      <header
        className="border-b bg-white/90 backdrop-blur sticky top-0 z-10"
        style={{ borderTopWidth: 4, borderTopColor: brandColor }}
      >
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {school?.logo_url ? (
              <img
                src={school.logo_url}
                alt={school.name}
                className="w-10 h-10 rounded-lg object-contain bg-white border"
                data-testid="school-teacher-logo"
              />
            ) : (
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center text-white"
                style={{ backgroundColor: brandColor }}
                data-testid="school-teacher-logo-fallback"
              >
                <SchoolIcon size={20} />
              </div>
            )}
            <div>
              <h1
                className="font-bold text-slate-900"
                data-testid="school-teacher-school-name"
              >
                {school?.name || 'Your School'}
              </h1>
              <p className="text-xs text-slate-500">
                <span data-testid="school-teacher-email">{me?.email}</span>
                <span> · Teacher</span>
              </p>
            </div>
          </div>
          <Button
            variant="ghost" size="sm"
            onClick={handleLogout}
            data-testid="school-teacher-logout-btn"
          >
            <LogOut size={16} className="mr-1.5" /> Logout
          </Button>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* CUM-4: drowning alerts surface near the top. Needs-help feed lives
            on the per-assessment status page (avoid class-wide volume noise). */}
        <div className="space-y-3 mb-6">
          <DrowningAlertCard />
        </div>

        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <h2
              className="text-2xl font-bold text-slate-900"
              data-testid="school-teacher-welcome"
            >
              Welcome{teacherName ? `, ${teacherName}` : ''} 👋
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              Your assigned classes appear below. Student lists and chat unlock soon.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline" size="sm"
              onClick={() => navigate('/school-teacher/assessments')}
              data-testid="open-assessments-btn"
            >
              <FileText size={14} className="mr-1.5" /> Assessments
            </Button>
            <Select value={filterYear} onValueChange={handleYearChange}>
              <SelectTrigger
                className="w-[140px]"
                data-testid="ay-filter-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ACADEMIC_YEARS.map((ay) => (
                  <SelectItem key={ay} value={ay} data-testid={`ay-option-${ay}`}>
                    {ay}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline" size="sm"
              onClick={handleRefresh}
              disabled={listLoading}
              data-testid="refresh-btn"
            >
              <RefreshCw size={14} className={listLoading ? 'animate-spin' : ''} />
            </Button>
          </div>
        </div>

        <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
          My Classes
        </h3>

        {listLoading ? (
          <SkeletonGrid />
        ) : assignments.length === 0 ? (
          <EmptyState onRefresh={handleRefresh} />
        ) : (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
            data-testid="class-cards-grid"
          >
            {assignments.map((a) => (
              <ClassCard
                key={a.assignment_id}
                assignment={a}
                brandColor={brandColor}
              />
            ))}
          </div>
        )}

        <p className="text-xs text-slate-400 mt-12">
          Need a class added or removed? Contact your school admin.
        </p>
      </main>
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
      data-testid="loading-skeleton"
    >
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="bg-white border rounded-xl p-5 animate-pulse"
        >
          <div className="h-5 w-20 bg-slate-200 rounded mb-3" />
          <div className="h-4 w-32 bg-slate-100 rounded mb-2" />
          <div className="h-4 w-24 bg-slate-100 rounded mb-6" />
          <div className="h-9 w-full bg-slate-100 rounded" />
        </div>
      ))}
    </div>
  );
}

function EmptyState({ onRefresh }) {
  return (
    <div
      className="border-2 border-dashed border-slate-200 rounded-2xl py-16 px-6 text-center bg-white/60"
      data-testid="empty-state-illustration"
    >
      <div className="mx-auto w-14 h-14 rounded-full bg-indigo-50 flex items-center justify-center mb-4">
        <Inbox size={26} className="text-indigo-500" />
      </div>
      <p className="text-slate-900 font-semibold text-lg">No classes assigned yet</p>
      <p className="text-sm text-slate-600 mt-1 max-w-md mx-auto">
        Your school admin will assign you classes shortly. You'll receive an email once they do.
      </p>
      <Button
        variant="outline" size="sm"
        onClick={onRefresh}
        className="mt-5"
        data-testid="empty-state-refresh-btn"
      >
        <RefreshCw size={14} className="mr-1.5" />
        Refresh
      </Button>
    </div>
  );
}

function ClassCard({ assignment, brandColor }) {
  const navigate = useNavigate();
  const cls = assignment.school_class;
  const subj = assignment.subject;
  return (
    <div
      className="bg-white border rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow flex flex-col"
      data-testid={`class-card-${assignment.assignment_id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center text-white"
            style={{ backgroundColor: brandColor }}
          >
            <GraduationCap size={18} />
          </div>
          <div>
            <div
              className="font-bold text-lg text-slate-900 leading-tight"
              data-testid={`class-card-name-${assignment.assignment_id}`}
            >
              {cls?.name || '—'}
            </div>
            <div
              className="text-xs text-slate-500"
              data-testid={`class-card-grade-${assignment.assignment_id}`}
            >
              Grade {cls?.grade || '—'}{cls?.section ? ` · Section ${cls.section}` : ''}
            </div>
          </div>
        </div>
        {subj && (
          <Badge
            variant="outline"
            data-testid={`class-card-subject-${assignment.assignment_id}`}
            style={{
              borderColor: subj.color || '#CBD5E1',
              color: subj.color || '#475569',
            }}
          >
            {subj.name}
          </Badge>
        )}
      </div>
      <div className="text-xs text-slate-400 mb-4">
        {cls?.academic_year || ''}
      </div>

      <div className="mt-auto space-y-2">
        <Button
          variant="outline"
          className="w-full"
          onClick={() => navigate(`/school-teacher/class/${cls?.school_class_id}`)}
          disabled={!cls?.school_class_id}
          data-testid={`view-students-btn-${assignment.assignment_id}`}
        >
          <Users size={14} className="mr-1.5" />
          View Students
        </Button>
        <Button
          variant="ghost" size="sm"
          className="w-full"
          onClick={() => navigate(`/school-teacher/class/${cls?.school_class_id}/insights`)}
          disabled={!cls?.school_class_id}
          data-testid={`class-card-insights-btn-${cls?.school_class_id}`}
        >
          <BarChart3 size={14} className="mr-1.5" />
          Class Insights
        </Button>
      </div>
    </div>
  );
}
